/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'
import { Button } from '../../helpers/ToolbarButtons'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  backPageLink?: string
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
  pageButtons?: Array<Button>
  setPageButtons: (_buttons: Array<Button>) => void
  setBackPageLink: (_link: string) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
  setPageButtons: (_buttons: Array<Button>) => {},
  setBackPageLink: (_link: string) => {}
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])
  const [pageButtons, setPageButtons] = useState<Array<Button>>([])
  const [backPageLink, setBackPageLink] = useState('')
  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    pageButtons,
    setPageButtons,
    backPageLink,
    setBackPageLink
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
}

type ToolbarProps = {
  buttons?: Array<Button>
}

const BackPageLink = ({link}: {link: string}) => {
  const {setBackPageLink} = usePageData()
  
  useEffect(() => {
    setBackPageLink(link)
    return () => {
      setBackPageLink('')
    }
  }, [link, setBackPageLink])
  
  return <></>
}

const PageTitle: FC<Props & WithChildren> = ({children, description, breadcrumbs}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  return <></>
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

const PageToolbar: FC<ToolbarProps> = ({buttons}) => {
  const {setPageButtons} = usePageData()
  useEffect(() => {
    if(buttons) {
      setPageButtons(buttons)
    }
    return () => {
      setPageButtons([])
    }
  }, [buttons])
  return <></>
}

export {PageDescription, PageTitle, BackPageLink, PageDataProvider, PageToolbar, usePageData}
